import React, { Component } from "react";
import {
  GET,
  GET_FILE,
  alert,
  saveAs,
  loading,
  NavbarCustomer,
  t,
  sortOptions,
} from "../../components/CustomComponent.js"; // ปรับ Path ให้ตรงกับโปรเจกต์ของคุณ
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import { getProfile } from "../../utils/localStorage";
import { isCustomerScopeReady } from "../../utils/customerScope";
import {
  REPORT_PERIODS,
  CHART_COLORS,
  formatDate,
  formatMinutes,
  formatMonthLabel,
  formatPercent,
  statusColor,
  translateKey
} from "../../utils/customerReport";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
};

const DONUT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: { legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } } },
};

// ความเร่งด่วนไม่ได้เก็บสีไว้ที่ระดับรายงาน - ไล่สีตามคำที่พบบ่อยเพื่อให้ badge อ่านง่ายเหมือนเดิม
const PRIORITY_COLOR = (priority) => {
  const value = String(priority || "").toLowerCase();
  if (/urgent|critical|high|สูง|ด่วน/.test(value)) return "danger";
  if (/medium|normal|กลาง|ปกติ/.test(value)) return "warning";
  return "primary";
};

// สีของโดนัท "สถานะ" ใช้ค่าเดียวกับสีการ์ดสรุปด้านบน (ค่าสี Bootstrap danger/warning/success)
// เพื่อให้อ่านคู่กันแล้วสีตรงกัน ไม่ใช่คนละโทนเหมือนตอนใช้ CHART_COLORS
const STATUS_CHART_COLOR = {
  pending: "#dc3545",
  in_progress: "#ffc107",
  success: "#198754",
  closed: "#6c757d",
  default: "#6c757d",
};

// API เวอร์ชันก่อนหน้าส่งกลุ่มงานที่จบแล้วมาเป็นคีย์ "completed" - แปลงเป็น "success" ให้ตรงกับการ์ด
// ไม่งั้นถ้าเซิร์ฟเวอร์ยังไม่ได้อัปเดต กราฟจะขึ้นคำว่า Completed และได้สีเทาของค่า default
const normalizeStatusKey = (key) => (key === "completed" ? "success" : key);

export default class ServiceSummaryReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // -----------------------------------
      // Filter States
      // -----------------------------------
      filter_period: "last_30_days",
      filter_status: "",
      filter_category: "",

      summary: {
        total: 0,
        total_change_percent: null,
        pending: 0,
        pending_rate: 0,
        in_progress: 0,
        in_progress_rate: 0,
        success: 0,
        success_rate: 0
      },

      trend: [],
      status_breakdown: [],
      data: [],
      lookups: { categories: [] },
      generated_at: null,
      exporting: "",
      loaded: false
    };
  }

  async componentDidMount() {
    if (!isCustomerScopeReady(getProfile())) {
      loading(false);
      return;
    }

    await Promise.all([this.GetLookups(), this.Get()]);
    loading(false);
  }

  GetLookups = async () => {
    const result = await GET("customer/reports/lookups");
    if (result?.status === true) this.setState({ lookups: result.data || { categories: [] } });
  };

  filterParams = () => ({
    period: this.state.filter_period,
    status: this.state.filter_status || "",
    category_uid: this.state.filter_category || "",
  });

  Get = async () => {
    loading(true);

    const result = await GET("customer/reports/service-summary", { ...this.filterParams(), page_size: 100 });

    if (result?.status === true) {
      const data = result.data || {};
      this.setState({
        summary: data.summary || this.state.summary,
        trend: Array.isArray(data.trend) ? data.trend : [],
        status_breakdown: Array.isArray(data.status_breakdown)
          ? data.status_breakdown.map((row) => ({ ...row, key: normalizeStatusKey(row.key) }))
          : [],
        data: Array.isArray(data.data) ? data.data : [],
        generated_at: data.generated_at || null,
        loaded: true
      });
    }

    loading(false);
  };

  exportFile = async (format) => {
    this.setState({ exporting: format });

    const result = await GET_FILE("customer/reports/service-summary/export", {
      ...this.filterParams(),
      format,
      lang: localStorage.getItem("lang") === "EN" ? "en" : "th",
    });

    this.setState({ exporting: "" });

    if (result?.status !== true) {
      alert("", "error", t("error"), result?.message || t("export_failed"));
      return;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(result.blob, `service-summary_${stamp}.${format === "pdf" ? "pdf" : "xlsx"}`);
  };

  trendData = () => ({
    labels: this.state.trend.map((row) => formatMonthLabel(row.month)),
    datasets: [
      {
        label: t("total_requests"),
        data: this.state.trend.map((row) => row.total),
        borderColor: CHART_COLORS[0],
        backgroundColor: "rgba(59, 130, 246, 0.15)",
        fill: true,
        tension: 0.35,
      },
      {
        label: t("success"),
        data: this.state.trend.map((row) => row.success ?? row.completed ?? 0),
        borderColor: CHART_COLORS[1],
        backgroundColor: "rgba(34, 197, 94, 0.12)",
        fill: true,
        tension: 0.35,
      },
    ],
  });

  breakdownData = () => ({
    labels: this.state.status_breakdown.map((row) => t(row.key)),
    datasets: [
      {
        data: this.state.status_breakdown.map((row) => row.count),
        backgroundColor: this.state.status_breakdown.map((row) => STATUS_CHART_COLOR[row.key] || STATUS_CHART_COLOR.default),
        borderWidth: 0,
      },
    ],
  });

  changeText = () => {
    const value = this.state.summary.total_change_percent;
    if (value === null || value === undefined) return t("no_previous_period");
    return `${value > 0 ? "+" : ""}${value}% ${t("vs_previous")}`;
  };

  render() {
    const { summary, data, trend, status_breakdown, lookups, filter_period, filter_status, filter_category, exporting, generated_at, loaded } = this.state;
    const hasBreakdown = status_breakdown.some((row) => row.count > 0);

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarCustomer />
        <div className="body p-4 mt-3">
          
          {/* Header Section */}
          <div className="mb-4">
            <h3 className="fw-bold mb-1 text-dark">{t("service_summary_report")}</h3>
            <div className="text-secondary text-14">{t("overview_desc")}</div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white rounded-4 shadow-sm p-3 mb-4 d-flex flex-wrap align-items-end gap-3 border border-light">
            <div className="flex-grow-1" style={{ minWidth: '200px' }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">{t("date_range")}</label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_period}
                onChange={(e) => this.setState({ filter_period: e.target.value })}
              >
                {REPORT_PERIODS.map((period) => (
                  <option key={period.value} value={period.value}>{t(period.label)}</option>
                ))}
              </select>
            </div>
            <div className="flex-grow-1" style={{ minWidth: '200px' }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">{t("status")}</label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_status}
                onChange={(e) => this.setState({ filter_status: e.target.value })}
              >
                {/* ตัวเลือกตรงกับการ์ดสรุปด้านล่างพอดี - ใบที่ถูกปฏิเสธ/ยกเลิกดูได้จาก "ทั้งหมด" */}
                <option value="">{t("all_status")}</option>
                <option value="pending">{t("pending")}</option>
                <option value="in_progress">{t("in_progress")}</option>
                <option value="success">{t("success")}</option>
              </select>
            </div>
            <div className="flex-grow-1" style={{ minWidth: '200px' }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">{t("category")}</label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_category}
                onChange={(e) => this.setState({ filter_category: e.target.value })}
              >
                <option value="">{t("all_categories")}</option>
                {sortOptions(lookups.categories, { by: "name" }).map((category) => (
                  <option key={category.uid} value={category.uid}>{category.name}</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary px-4 fw-bold rounded-3 shadow-sm border-0 d-flex align-items-center"
              style={{ height: '42px' }}
              onClick={() => this.Get()}
            >
              <span className="icon me-2">{"\uf0b0"}</span> {t("apply_filters")}
            </button>
          </div>

          {/* KPI Summary Cards - ทั้งหมด / รอดำเนินการ (pending, open) / กำลังดำเนินการ (pending_approval, approved) / เสร็จสิ้น (success) */}
          <div className="row g-3 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-primary rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-primary text-12 fw-bold mb-1">{t("total_requests")}</div>
                <h2 className="fw-bold text-dark mb-1">{summary.total}</h2>
                <div className="text-primary text-10 fw-bold">{this.changeText()}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-danger rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-danger text-12 fw-bold mb-1">{t("pending")}</div>
                <h2 className="fw-bold text-dark mb-1">{summary.pending}</h2>
                <div className="text-danger text-10 fw-bold">{formatPercent(summary.pending_rate)} {t("of_total")}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-warning rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-warning text-12 fw-bold mb-1">{t("in_progress")}</div>
                <h2 className="fw-bold text-dark mb-1">{summary.in_progress}</h2>
                <div className="text-warning text-10 fw-bold">{formatPercent(summary.in_progress_rate)} {t("of_total")}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-success rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-success text-12 fw-bold mb-1">{t("success")}</div>
                <h2 className="fw-bold text-dark mb-1">{summary.success ?? summary.completed ?? 0}</h2>
                <div className="text-success text-10 fw-bold">{formatPercent(summary.success_rate ?? summary.completed_rate)} {t("of_total")}</div>
              </div>
            </div>
          </div>

          {/* Graphs Area */}
          <div className="row g-4 mb-4">
            <div className="col-lg-8">
              <div className="card border-0 rounded-4 shadow-sm p-4 bg-white h-100">
                <h6 className="fw-bold text-dark mb-4">{t("request_trend")}</h6>
                <div style={{ height: '250px' }}>
                  {trend.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center bg-light rounded-3 h-100 text-secondary">
                      {loaded ? t("no_data") : t("loading")}
                    </div>
                  ) : (
                    <Line data={this.trendData()} options={CHART_OPTIONS} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 rounded-4 shadow-sm p-4 bg-white h-100">
                <h6 className="fw-bold text-dark mb-4">{t("status_breakdown")}</h6>
                <div style={{ height: '250px' }}>
                  {!hasBreakdown ? (
                    <div className="d-flex align-items-center justify-content-center h-100 text-secondary">
                      {loaded ? t("no_data") : t("loading")}
                    </div>
                  ) : (
                    <Doughnut data={this.breakdownData()} options={DONUT_OPTIONS} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Details Table */}
          <div className="card border-0 rounded-4 shadow-sm pb-2 overflow-hidden bg-white">
            <div className="p-4 border-bottom border-light">
              <h6 className="fw-bold text-dark mb-0">{t("service_request_details")}</h6>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr className="text-secondary fw-bold text-12 border-bottom border-light bg-light bg-opacity-50">
                    <th className="py-3 px-4 border-0">{t("request_id")}</th>
                    <th className="py-3 border-0">{t("date")}</th>
                    <th className="py-3 border-0">{t("category")}</th>
                    <th className="py-3 border-0">{t("description")}</th>
                    <th className="py-3 border-0 text-center">{t("priority")}</th>
                    <th className="py-3 border-0 text-center">{t("status")}</th>
                    <th className="py-3 border-0 text-center">{t("resolution_time")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary py-4">{loaded ? t("no_data") : t("loading")}</td>
                    </tr>
                  ) : data.map((item) => (
                    <tr key={item.uid} className="border-bottom border-light">
                      <td className="py-4 px-4 fw-bold text-primary font-monospace">{item.wr_number || "-"}</td>
                      <td className="text-secondary text-13">{formatDate(item.date)}</td>
                      <td className="text-secondary text-13">{item.category || "-"}</td>
                      <td className="text-dark text-13 fw-medium">{item.description || "-"}</td>
                      <td className="text-center">
                        <span className={`badge bg-${PRIORITY_COLOR(item.priority)}-light text-${PRIORITY_COLOR(item.priority)} rounded-pill px-3 py-1 fw-bold text-10`}>
                          {item.priority || "-"}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`badge bg-${statusColor(item.status)}-light text-${statusColor(item.status)} rounded-pill px-3 py-1 fw-bold text-10`}>
                          {translateKey(item.status)}
                        </span>
                      </td>
                      <td className="text-center text-secondary text-13 fw-bold">{formatMinutes(item.resolution_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="d-flex justify-content-between align-items-center mt-4 bg-white p-3 rounded-4 shadow-sm border border-light">
            <div className="text-secondary text-12">
              {t("report_generated")}: {generated_at ? `${formatDate(generated_at)}` : "-"}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-white border px-3 py-2 text-danger fw-bold rounded-3 d-flex align-items-center shadow-none"
                disabled={!!exporting}
                onClick={() => this.exportFile("pdf")}
              >
                <span className="icon me-2">{"\uf1c1"}</span> {exporting === "pdf" ? t("loading") : t("export_pdf")}
              </button>
              <button
                className="btn btn-white border px-3 py-2 text-success fw-bold rounded-3 d-flex align-items-center shadow-none"
                disabled={!!exporting}
                onClick={() => this.exportFile("excel")}
              >
                <span className="icon me-2">{"\uf1c3"}</span> {exporting === "excel" ? t("loading") : t("export_excel")}
              </button>
              <button
                className="btn btn-primary px-4 py-2 fw-bold rounded-3 shadow-sm border-0 d-flex align-items-center"
                onClick={() => window.print()}
              >
                <span className="icon me-2">{"\uf02f"}</span> {t("print_report")}
              </button>
            </div>
          </div>

        </div>
      </div>
    );
  }
}
